/**
 * StreakHero — full-width hero bar that sits *above* the dashboard
 * greeting. The single highest-leverage retention surface on the page
 * (Feb 2026 overhaul).
 *
 * Three personalities driven by /dashboard/hero-context:
 *
 *   • current=0  → green "Start a streak today" CTA (no urgency, but
 *                  builds the muscle memory that "this lives here").
 *   • is_active_today → satisfied flame + "Day N · safe" badge.
 *   • !is_active_today (streak alive but today not yet logged):
 *       - safe     (>8h left)  — neutral amber
 *       - warning  (3–8h left) — orange pulsing
 *       - critical (≤3h left)  — red urgency, animated flame, "Save
 *                                 your streak — 3h left" copy
 *
 * The flame uses a layered SVG + framer-motion pulse so it reads as
 * alive without being noisy. Sound and confetti are intentionally NOT
 * fired here — they belong to the celebration modal (one-shot moments,
 * not ambient).
 */
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Flame, Sparkles, ArrowRight, ShieldAlert } from 'lucide-react';
import { useHeroContext } from '../../hooks/useHeroContext';

const FlameIcon = ({ size = 56, intense = false }) => (
  <motion.div
    initial={{ scale: 0.9 }}
    animate={intense ? { scale: [0.95, 1.06, 0.95] } : { scale: [0.98, 1.02, 0.98] }}
    transition={{ duration: intense ? 1.2 : 2.4, repeat: Infinity, ease: 'easeInOut' }}
    className="relative flex items-center justify-center"
    style={{ width: size, height: size }}
  >
    <div
      className={`absolute inset-0 rounded-full blur-xl ${intense ? 'bg-red-400/60' : 'bg-amber-400/40'}`}
      aria-hidden
    />
    <Flame
      className={`relative ${intense ? 'text-red-500' : 'text-amber-500'}`}
      style={{ width: size * 0.6, height: size * 0.6, strokeWidth: 2.4 }}
      aria-hidden
    />
  </motion.div>
);

const StreakHero = () => {
  const { data, isLoading } = useHeroContext();
  if (isLoading || !data) return null;

  const { streak } = data;
  const current = streak?.current || 0;
  const longest = streak?.longest || 0;
  const isActiveToday = !!streak?.is_active_today;
  const hoursLeft = streak?.hours_until_save;
  const urgency = streak?.urgency || 'safe';

  // ─── No streak yet — quiet "begin" surface ───────────────────────────
  if (current === 0) {
    return (
      <Link to="/review" data-testid="streak-hero-empty">
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mb-6 rounded-2xl border border-[var(--f500-line)] bg-gradient-to-r from-[#F0FDF4] via-white to-[#F0FDF4] p-5 sm:p-6 flex items-center gap-5 hover:shadow-lg transition-shadow group"
        >
          <FlameIcon size={56} />
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-bold tracking-[0.18em] uppercase text-emerald-700">No streak yet</p>
            <h2 className="font-heading text-xl sm:text-2xl font-bold text-[var(--isp-heading)] mt-0.5">
              Start your streak today.
            </h2>
            <p className="text-sm text-[var(--isp-body)] mt-1">
              Review one flashcard, record one lecture, or open Study Buddy — that's it.
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-emerald-600 group-hover:translate-x-1 transition-transform hidden sm:block" aria-hidden />
        </motion.div>
      </Link>
    );
  }

  // ─── Streak alive + today already counted ─────────────────────────────
  if (isActiveToday) {
    const flair = current >= 30 ? '🚀 Unstoppable' : current >= 14 ? '🔥 On fire' : current >= 7 ? '💪 One week' : '✨ Building';
    return (
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="mb-6 rounded-2xl border border-amber-200 bg-gradient-to-r from-[#FFF8E1] via-white to-[#FFF8E1] p-5 sm:p-6 flex items-center gap-5"
        data-testid="streak-hero-safe"
      >
        <FlameIcon size={64} />
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-bold tracking-[0.18em] uppercase text-amber-700 flex items-center gap-2">
            {flair} · safe today
          </p>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[var(--isp-heading)] mt-0.5 leading-tight">
            <span className="text-amber-600">{current}</span>-day streak
          </h2>
          <p className="text-sm text-[var(--isp-body)] mt-1">
            Personal best: <strong className="text-[var(--isp-heading)]">{longest}</strong> days.
            {current === longest ? ' You\'re writing history right now.' : ''}
          </p>
        </div>
        <Link
          to="/achievements"
          className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 hover:text-amber-900 whitespace-nowrap"
          data-testid="streak-hero-trophy-link"
        >
          <Sparkles className="w-3.5 h-3.5" /> View badges
        </Link>
      </motion.div>
    );
  }

  // ─── Streak alive, today NOT counted — urgency surface ────────────────
  const intense = urgency === 'critical';
  const palette = urgency === 'critical'
    ? { ring: 'border-red-300', bg: 'from-red-50 via-white to-red-50', tint: 'text-red-700', accent: 'bg-red-500 hover:bg-red-600' }
    : urgency === 'warning'
    ? { ring: 'border-orange-200', bg: 'from-orange-50 via-white to-orange-50', tint: 'text-orange-700', accent: 'bg-orange-500 hover:bg-orange-600' }
    : { ring: 'border-amber-200', bg: 'from-amber-50 via-white to-amber-50', tint: 'text-amber-700', accent: 'bg-amber-500 hover:bg-amber-600' };

  const hoursLabel = hoursLeft === null || hoursLeft === undefined
    ? 'before midnight'
    : hoursLeft <= 1 ? '< 1 hour left' : `${hoursLeft} hours left`;

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className={`mb-6 rounded-2xl border ${palette.ring} bg-gradient-to-r ${palette.bg} p-5 sm:p-6 flex items-center gap-5 ${intense ? 'shadow-[0_10px_30px_-15px_rgba(239,68,68,0.4)]' : ''}`}
      data-testid={`streak-hero-${urgency}`}
    >
      <FlameIcon size={64} intense={intense} />
      <div className="flex-1 min-w-0">
        <p className={`text-[11px] font-bold tracking-[0.18em] uppercase ${palette.tint} flex items-center gap-1.5`}>
          {intense && <ShieldAlert className="w-3.5 h-3.5" aria-hidden />}
          {intense ? 'Save your streak' : 'Streak in danger'} · {hoursLabel}
        </p>
        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[var(--isp-heading)] mt-0.5 leading-tight">
          {current}-day streak — <span className={palette.tint}>one action away.</span>
        </h2>
        <p className="text-sm text-[var(--isp-body)] mt-1">
          Review a card, record a lecture, or chat with Study Buddy to save it.
        </p>
      </div>
      <Link to="/review" className="hidden sm:block">
        <button
          className={`px-5 py-3 rounded-xl text-white font-semibold text-sm ${palette.accent} flex items-center gap-2 whitespace-nowrap transition-colors`}
          data-testid="streak-hero-save-btn"
        >
          Save streak <ArrowRight className="w-4 h-4" />
        </button>
      </Link>
    </motion.div>
  );
};

export default StreakHero;
